import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Paper,
  Box,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  IconButton
} from '@mui/material';
import { Delete as DeleteIcon, Edit as EditIcon, AccessTime, ContentCopy as ContentCopyIcon, ManageHistory } from '@mui/icons-material';
import { apiRequest } from '../api/api';
import { formatTimeRange } from '../utils/timeUtils';
import { authService } from '../api/authService';

// Colore della pagina Gestione Turni
const PAGE_COLOR = '#F44336';

interface Turno {
  id: number;
  neg: string;
  giornoSettimana: string;
  turno: string;
  oraInizio: string;
  oraFine: string;
}

interface Negozio {
  neg: string;
  negozio: string;
}

interface TurnoRow {
  giorno: string;
  mattina: string | null;
  pomeriggio: string | null;
  intermezzo: string | null;
  records: {
    mattina: Turno | null;
    pomeriggio: Turno | null;
    intermezzo: Turno | null;
  };
}

const TurnoList: React.FC = () => {
  const [turni, setTurni] = useState<Turno[]>([]);
  const [negozi, setNegozi] = useState<Negozio[]>([]);
  const [selectedNegozio, setSelectedNegozio] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [open, setOpen] = useState<boolean>(false);
  const [rows, setRows] = useState<TurnoRow[]>([]);
  const [editingTurno, setEditingTurno] = useState<{
    row: number;
    tipo: string;
    turno?: Turno;
  } | null>(null);
  const [formData, setFormData] = useState<{
    oraInizio: string;
    oraFine: string;
  }>({
    oraInizio: '',
    oraFine: ''
  });

  // Ottieni il ruolo dell'utente corrente
  const currentUser = authService.getCurrentUser();
  const userRole = currentUser?.ruolo || '';
  // Include Dipendente, Linker, Maker, Referente - i permessi sono determinati da AbilitaInsOrari
  const isLimitedRole = ['Dipendente', 'Linker', 'Maker', 'Referente'].includes(userRole);
  const canDuplicateWeek = ['Admin', 'Coordinatore', 'ResponsabileOrari'].includes(userRole) || (isLimitedRole && currentUser?.abilitaInsOrari);

  const giorni = ['Lunedì', 'Martedì', 'Mercoledì', 'Giovedì', 'Venerdì', 'Sabato', 'Domenica'];

  useEffect(() => {
    fetchNegozi();
  }, []);

  useEffect(() => {
    if (selectedNegozio) {
      setError('');
      setSuccess('');
      fetchTurni();
    }
  }, [selectedNegozio]);

  useEffect(() => {
    createRows();
  }, [turni]);

  const fetchNegozi = async () => {
    try {
      const data = await apiRequest<Negozio[]>('/negozi/accessibili', {
        method: 'GET',
      });
      setNegozi(data);
      // Seleziona automaticamente il primo negozio
      if (data.length > 0 && !selectedNegozio) {
        setSelectedNegozio(data[0].neg);
      }
    } catch (err) {
      setError('Errore nel caricamento dei negozi');
      console.error('Error fetching negozi:', err);
    }
  };

  const fetchTurni = async () => {
    if (!selectedNegozio) return;
    
    setLoading(true);
    setError('');
    try {
      const data = await apiRequest(`/turni/negozio/${selectedNegozio}`, {
        method: 'GET',
      });
      setTurni(data);
    } catch (err) {
      setError('Errore nel caricamento dei turni');
      console.error('Error fetching turni:', err);
    } finally {
      setLoading(false);
    }
  };

  const createRows = () => {
    const newRows: TurnoRow[] = giorni.map(giorno => {
      const mattinaRecord = turni.find(t => t.giornoSettimana === giorno && t.turno === 'Mattina');
      const pomeriggioRecord = turni.find(t => t.giornoSettimana === giorno && t.turno === 'Pomeriggio');
      const intermezzoRecord = turni.find(t => t.giornoSettimana === giorno && t.turno === 'Intermezzo');

      return {
        giorno,
        mattina: mattinaRecord ? formatTimeRange(mattinaRecord.oraInizio, mattinaRecord.oraFine, '/') : null,
        pomeriggio: pomeriggioRecord ? formatTimeRange(pomeriggioRecord.oraInizio, pomeriggioRecord.oraFine, '/') : null,
        intermezzo: intermezzoRecord ? formatTimeRange(intermezzoRecord.oraInizio, intermezzoRecord.oraFine, '/') : null,
        records: {
          mattina: mattinaRecord || null,
          pomeriggio: pomeriggioRecord || null,
          intermezzo: intermezzoRecord || null
        }
      };
    });
    setRows(newRows);
  };

  const handleCellEdit = (rowIndex: number, tipo: 'mattina' | 'pomeriggio' | 'intermezzo') => {
    const row = rows[rowIndex];
    const turno = row.records[tipo];
    
    setEditingTurno({ row: rowIndex, tipo, turno: turno || undefined });
    setFormData({
      oraInizio: turno?.oraInizio || '',
      oraFine: turno?.oraFine || ''
    });
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditingTurno(null);
    setFormData({ oraInizio: '', oraFine: '' });
  };

  const handleSave = async () => {
    if (!editingTurno || !selectedNegozio) return;

    const row = rows[editingTurno.row];
    const turnoData = {
      neg: selectedNegozio,
      giornoSettimana: row.giorno,
      turno: editingTurno.tipo.charAt(0).toUpperCase() + editingTurno.tipo.slice(1),
      oraInizio: formData.oraInizio,
      oraFine: formData.oraFine
    };

    try {
      setError('');
      setSuccess('');
      
      if (editingTurno.turno) {
        // Update existing turno
        await apiRequest(`/turni/${editingTurno.turno.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(turnoData),
        });
      } else {
        // Create new turno
        await apiRequest('/turni', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(turnoData),
        });
      }
      fetchTurni();
      handleClose();
    } catch (err) {
      setError('Errore nel salvataggio del turno');
      console.error('Error saving turno:', err);
    }
  };

  const handleDuplicateWeek = async () => {
    if (!selectedNegozio) return;

    if (window.confirm('Sei sicuro di voler duplicare i turni del Lunedì per tutti gli altri giorni della settimana? Questo sovrascriverà tutti i turni esistenti per Martedì-Domenica.')) {
      try {
        setLoading(true);
        setError('');
        setSuccess('');
        
        const response = await apiRequest<string>(`/turni/duplica-settimana?negozio=${selectedNegozio}`, {
          method: 'POST',
        });
        
        setSuccess(typeof response === 'string' ? response : 'Turni duplicati con successo');
        fetchTurni(); // Ricarica i turni per mostrare le modifiche
      } catch (err: any) {
        setError(err.message || 'Errore durante la duplicazione dei turni');
        console.error('Error duplicating week:', err);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleDeleteFromDialog = async () => {
    if (!editingTurno?.turno) return;

    if (window.confirm('Sei sicuro di voler eliminare questo turno?')) {
      try {
        await apiRequest(`/turni/${editingTurno.turno.id}`, {
          method: 'DELETE',
        });
        fetchTurni();
        handleClose();
      } catch (err) {
        setError('Errore nell\'eliminazione del turno');
        console.error('Error deleting turno:', err);
      }
    }
  };

  // Funzione per eliminare turno singolo (attualmente non utilizzata)
  // const handleDelete = async (rowIndex: number, tipo: 'mattina' | 'pomeriggio' | 'intermezzo') => {
  //   const row = rows[rowIndex];
  //   const turno = row.records[tipo];
  //   
  //   if (!turno) return;

  //   if (window.confirm('Sei sicuro di voler eliminare questo turno?')) {
  //     try {
  //       await apiRequest(`/turni/${turno.id}`, {
  //         method: 'DELETE',
  //       });
  //       fetchTurni();
  //     } catch (err) {
  //       setError('Errore nell\'eliminazione del turno');
  //       console.error('Error deleting turno:', err);
  //     }
  //   }
  // };

  const handleInputChange = (field: 'oraInizio' | 'oraFine') => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFormData({
      ...formData,
      [field]: event.target.value,
    });
  };

  if (loading && !selectedNegozio) {
    return (
      <Container maxWidth="lg">
        <Typography>Caricamento...</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl">
      <Box sx={{ mb: 3, textAlign: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
          <ManageHistory fontSize="large" sx={{ mr: 2, color: PAGE_COLOR }} />
          <Typography variant="h4" component="h1">
            Gestione Turni
          </Typography>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 3, alignItems: 'center', mb: 2 }}>
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>Seleziona Negozio</InputLabel>
            <Select
              value={selectedNegozio}
              label="Seleziona Negozio"
              onChange={(e) => setSelectedNegozio(e.target.value)}
            >
              {negozi.map((negozio) => (
                <MenuItem key={negozio.neg} value={negozio.neg}>
                  {negozio.neg} - {negozio.negozio}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          {canDuplicateWeek && selectedNegozio && (
            <Button
              variant="contained"
              onClick={handleDuplicateWeek}
              disabled={loading}
              startIcon={<ContentCopyIcon />}
              sx={{ 
                bgcolor: PAGE_COLOR,
                '&:hover': {
                  bgcolor: '#d32f2f',
                },
                '&:disabled': {
                  bgcolor: 'grey.300',
                  color: 'grey.500',
                }
              }}
            >
              Duplica Settimana
            </Button>
          )}
        </Box>

        {selectedNegozio && (
          <Typography variant="h6" sx={{ mb: 2 }}>
            Negozio: {selectedNegozio}
          </Typography>
        )}

        {loading && selectedNegozio && (
          <Typography>Caricamento turni...</Typography>
        )}

        {!selectedNegozio && (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: 'rgba(244, 67, 54, 0.1)' }}>
                  <TableCell sx={{ fontWeight: 'bold' }}>Giorno</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold' }}>Mattina</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold' }}>Pomeriggio</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold' }}>Intermezzo</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {giorni.map((giorno) => (
                  <TableRow key={giorno}>
                    <TableCell component="th" scope="row">
                      <strong>{giorno}</strong>
                    </TableCell>
                    <TableCell align="center" colSpan={3}>
                      <Typography variant="h6" color="text.secondary" sx={{ py: 4 }}>
                        Seleziona Negozio
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {!loading && selectedNegozio && rows.length > 0 && (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: 'rgba(244, 67, 54, 0.1)' }}>
                  <TableCell sx={{ fontWeight: 'bold' }}>Giorno</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold' }}>Mattina</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold' }}>Pomeriggio</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold' }}>Intermezzo</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row, index) => (
                  <TableRow key={row.giorno}>
                    <TableCell component="th" scope="row">
                      <strong>{row.giorno}</strong>
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            minWidth: 100, 
                            cursor: 'pointer', 
                            color: row.mattina ? 'text.primary' : 'text.secondary',
                            textDecoration: 'underline'
                          }}
                          onClick={() => handleCellEdit(index, 'mattina')}
                        >
                          {row.mattina || 'Clicca per aggiungere'}
                        </Typography>
                        {row.records.mattina && (
                          <IconButton
                            size="small"
                            onClick={() => handleCellEdit(index, 'mattina')}
                            sx={{
                              color: PAGE_COLOR,
                              '&:hover': {
                                bgcolor: 'rgba(244, 67, 54, 0.1)', // Hover con trasparenza
                              }
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            minWidth: 100, 
                            cursor: 'pointer', 
                            color: row.pomeriggio ? 'text.primary' : 'text.secondary',
                            textDecoration: 'underline'
                          }}
                          onClick={() => handleCellEdit(index, 'pomeriggio')}
                        >
                          {row.pomeriggio || 'Clicca per aggiungere'}
                        </Typography>
                        {row.records.pomeriggio && (
                          <IconButton
                            size="small"
                            onClick={() => handleCellEdit(index, 'pomeriggio')}
                            sx={{
                              color: PAGE_COLOR,
                              '&:hover': {
                                bgcolor: 'rgba(244, 67, 54, 0.1)', // Hover con trasparenza
                              }
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            minWidth: 100, 
                            cursor: 'pointer', 
                            color: row.intermezzo ? 'text.primary' : 'text.secondary',
                            textDecoration: 'underline'
                          }}
                          onClick={() => handleCellEdit(index, 'intermezzo')}
                        >
                          {row.intermezzo || 'Clicca per aggiungere'}
                        </Typography>
                        {row.records.intermezzo && (
                          <IconButton
                            size="small"
                            onClick={() => handleCellEdit(index, 'intermezzo')}
                            sx={{
                              color: PAGE_COLOR,
                              '&:hover': {
                                bgcolor: 'rgba(244, 67, 54, 0.1)', // Hover con trasparenza
                              }
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingTurno?.turno ? 'Modifica Turno' : 'Nuovo Turno'} 
          {editingTurno?.tipo && ` - ${editingTurno.tipo.charAt(0).toUpperCase() + editingTurno.tipo.slice(1)}`}
          {editingTurno && (
            <Typography variant="subtitle1" color="text.secondary">
              {rows[editingTurno.row]?.giorno}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
            <TextField
              label="Ora Inizio"
              value={formData.oraInizio}
              onChange={handleInputChange('oraInizio')}
              fullWidth
              required
              type="time"
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Ora Fine"
              value={formData.oraFine}
              onChange={handleInputChange('oraFine')}
              fullWidth
              required
              type="time"
              InputLabelProps={{ shrink: true }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between', p: 2 }}>
          <Box>
            {editingTurno?.turno && (
              <Button 
                onClick={handleDeleteFromDialog}
                color="error"
                variant="outlined"
                startIcon={<DeleteIcon />}
              >
                Elimina
              </Button>
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button onClick={handleClose}>Annulla</Button>
            <Button 
              onClick={handleSave} 
              variant="contained"
              disabled={!formData.oraInizio || !formData.oraFine}
              sx={{
                bgcolor: PAGE_COLOR,
                color: 'white',
                '&:hover': {
                  bgcolor: '#D32F2F', // Versione più scura di #F44336
                },
                '&:disabled': {
                  bgcolor: 'grey.300',
                  color: 'grey.500',
                }
              }}
            >
              {editingTurno?.turno ? 'Salva Modifiche' : 'Crea Turno'}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default TurnoList;